import { ReactNode } from 'react'

import type { t } from '../../../i18n.js'
import type { Language } from '../../../types/i18n.js'
import type {
    Q12GroupResult,
    Q12HistoryPointData,
    Q12QuestionResult,
    Q12ReportData,
    Q12StatBlock,
    Q12TextAnswerBlock,
} from '../../../types/q12-report.js'
import { questionText } from '../../../utils/q12-question-text.js'
import {
    formatOutOf,
    Q12ParticipationBars,
    Q12ProgrammeOverview,
    type Q12ProgrammeOverviewProps,
    Q12QuestionTable,
    Q12TextAnswers,
    Q12Trend,
} from './q12-results.js'

/**
 * The body of the Q12 results report (TASK-538): campaign and team overview
 * cards, the campaign-wide and team trends, participation by team and the
 * per-question tables. Shared by the portal report (Raportid → Q12 tulemused)
 * and the admin results tool — the filter controls above it belong to each
 * app's container, which also owns loading and state.
 *
 * Purely presentational: data, copy and callbacks arrive as props. Team
 * selection is read from `data.selectedGroupIds`, which the server resolves.
 */

export type Q12ReportLabels = typeof t.portal.reports

export interface Q12ReportBodyProps {
    data: Q12ReportData
    lang: Language
    labels: Q12ReportLabels
    /** Formats an ISO date for display (each app has its own convention). */
    formatDate: (iso: string) => string
    /** Origin of the survey link, e.g. `window.location.origin`. */
    surveyOrigin: string
    linkCopy: { copied: boolean; onCopy: (url: string) => void }
    /** A trend point was clicked. */
    onSelectCampaign: (campaignId: number) => void
    /**
     * Extra content under each team's table (the admin tool adds the notes
     * editor and PDF export here). Without it, saved notes are shown read-only.
     */
    renderTeamExtras?: (group: Q12GroupResult) => ReactNode
}

const formatScore = (value: number | null): string => (value === null ? '—' : value.toFixed(2))

const scoreDelta = (current: number | null, previous: number | null): number | null =>
    current !== null && previous !== null ? Math.round((current - previous) * 100) / 100 : null

export const Q12ReportBody = ({
    data,
    lang,
    labels: r,
    formatDate,
    surveyOrigin,
    linkCopy,
    onSelectCampaign,
    renderTeamExtras,
}: Q12ReportBodyProps) => {
    const activeCampaignId = data.campaign?.id
    const selectedCampaign = data.campaign
    const resultsHidden = data.resultsHidden === true
    const company = data.company
    // `summary` covers the selected team(s); `company` the whole campaign.
    const summary = data.summary
    const teams = data.teams ?? []
    const selectedGroupIds = data.selectedGroupIds ?? []
    const selectedTeams = teams.filter((team) => selectedGroupIds.includes(team.id))
    const hasTeam = selectedTeams.length > 0
    const teamNames = selectedTeams.map((team) => team.name).join(', ')

    const deltaSuffix = data.compareCampaign && !resultsHidden ? r.q12VsPrevious(lang) : undefined
    const participationDelta = (current: number | null, previous: number | null) =>
        resultsHidden || current === null || previous === null ? null : current - previous
    // Ring + tiles shared by both overview cards; scores stay hidden while
    // the campaign is open below full participation.
    const statsFor = (block: Q12StatBlock) => ({
        ring: {
            percent: block.participationPct,
            label: r.q12Participation(lang),
            sub: formatOutOf(block.respondents, block.eligible),
            delta: participationDelta(block.participationPct, block.previousParticipationPct),
            deltaSuffix,
        },
        tiles: [
            {
                label: r.q12RespondentsLabel(lang),
                value: String(block.respondents),
                sub: `/ ${block.eligible > 0 ? block.eligible : '—'}`,
            },
            ...(resultsHidden
                ? []
                : [
                      {
                          label: r.q12Overall(lang),
                          value: formatScore(block.overallAverage),
                          delta: scoreDelta(block.overallAverage, block.previousOverallAverage),
                          sub: data.compareCampaign
                              ? `${r.q12Previous(lang)}: ${formatScore(block.previousOverallAverage)}`
                              : undefined,
                      },
                  ]),
        ],
    })

    // Left card: the whole campaign.
    const programme: Q12ProgrammeOverviewProps | null =
        selectedCampaign && company
            ? (() => {
                  const state = selectedCampaign.state ?? 'closed'
                  const surveyUrl = `${surveyOrigin}/survey/${selectedCampaign.id}`
                  return {
                      title: r.q12Programme(lang),
                      status:
                          state === 'open'
                              ? { label: r.q12StatusOpen(lang), tone: 'active' as const }
                              : state === 'not-open'
                                ? { label: r.q12StatusUpcoming(lang), tone: 'upcoming' as const }
                                : { label: r.q12StatusClosed(lang), tone: 'ended' as const },
                      headline: selectedCampaign.name,
                      subline:
                          state === 'open'
                              ? r.q12EndsIn(selectedCampaign.daysUntilEnd ?? 0)(lang)
                              : state === 'not-open'
                                ? r.q12StartsOn(formatDate(selectedCampaign.start))(lang)
                                : r.q12EndedOn(formatDate(selectedCampaign.end))(lang),
                      stats: statsFor(company),
                      rows: [
                          {
                              label: r.q12Timeline(lang),
                              value: `${formatDate(selectedCampaign.start)} → ${formatDate(selectedCampaign.end)}`,
                          },
                      ],
                      link: {
                          label: r.q12SurveyLink(lang),
                          url: surveyUrl,
                          copyLabel: r.q12CopyLink(lang),
                          copiedLabel: r.q12Copied(lang),
                          copied: linkCopy.copied,
                          onCopy: () => linkCopy.onCopy(surveyUrl),
                      },
                  }
              })()
            : null
    // Right card: the selected team, or a hint until one is picked.
    const teamCard: Q12ProgrammeOverviewProps =
        hasTeam && summary
            ? { title: r.q12TeamOverview(lang), headline: teamNames, stats: statsFor(summary) }
            : {
                  title: r.q12TeamOverview(lang),
                  placeholder: teams.length > 0 ? r.q12SelectTeamHint(lang) : r.q12NoGroups(lang),
              }

    // Question text is shown in English regardless of the UI language — the
    // questionnaire's source language (Estonian only as a fallback).
    const questionTableLabels = {
        question: r.q12Question(lang),
        responses: r.q12Responses(lang),
        average: r.q12Average(lang),
        previous: r.q12Previous(lang),
    }
    const toTableRows = (rows: Q12QuestionResult[]) =>
        rows.map((q) => ({
            questionId: q.questionId,
            name: questionText({ name: q.questionName, name_en: q.questionNameEn }, 'en'),
            responseCount: q.responseCount,
            average: q.average,
            previousAverage: q.previousAverage,
            change: q.change,
        }))
    const toTrendPoints = (points: Q12HistoryPointData[]) =>
        points.map((h) => ({
            campaignId: h.campaignId,
            label: h.name,
            overallAverage: h.overallAverage,
            isActive: h.campaignId === activeCampaignId,
        }))
    const toTextBlocks = (blocks: Q12TextAnswerBlock[]) =>
        blocks.map((b) => ({
            questionId: b.questionId,
            name: questionText({ name: b.questionName, name_en: b.questionNameEn }, 'en'),
            answers: b.answers,
        }))
    const companyHistory = data.companyHistory ?? []
    const teamHistory = data.history ?? []
    const participation = data.participation ?? []
    const showPrevious = !!data.compareCampaign

    return (
        <>
            {programme && (
                <div className="q12r-headline">
                    <Q12ProgrammeOverview {...programme} />
                    <Q12ProgrammeOverview {...teamCard} />
                </div>
            )}

            {resultsHidden && selectedCampaign && (
                <p className="q12r-note">
                    {r.q12ResultsHiddenUntil(formatDate(selectedCampaign.end))(lang)}
                </p>
            )}

            {companyHistory.length > 1 && (
                <section className="q12r-section">
                    <h2 className="q12r-section__title">{r.q12History(lang)}</h2>
                    <Q12Trend points={toTrendPoints(companyHistory)} onSelect={onSelectCampaign} />
                </section>
            )}

            {hasTeam && teamHistory.length > 1 && (
                <section className="q12r-section">
                    <h2 className="q12r-section__title">
                        {r.q12HistoryTeam(lang)}: {teamNames}
                    </h2>
                    <Q12Trend points={toTrendPoints(teamHistory)} onSelect={onSelectCampaign} />
                </section>
            )}

            {participation.length > 0 && (
                <section className="q12r-section">
                    <h2 className="q12r-section__title">{r.q12ParticipationByGroup(lang)}</h2>
                    <Q12ParticipationBars
                        rows={participation.map((p) => ({
                            label: p.groupName,
                            done: p.respondents,
                            total: p.eligible,
                        }))}
                    />
                </section>
            )}

            {!resultsHidden && <p className="q12r-note">{r.q12AnonymityNote(lang)}</p>}
            {!resultsHidden && company && company.results.length > 0 && (
                <section className="q12r-section">
                    <h2 className="q12r-section__title">
                        {r.q12CompanyTitle(lang)}
                        {' — '}
                        {r.q12Respondents(company.respondents)(lang)}
                    </h2>
                    <Q12QuestionTable
                        rows={toTableRows(company.results)}
                        labels={questionTableLabels}
                        showPrevious={showPrevious}
                    />
                </section>
            )}
            {!resultsHidden && !hasTeam && company && (
                <Q12TextAnswers
                    title={r.q12TextAnswers(lang)}
                    blocks={toTextBlocks(company.textAnswers)}
                    countLabel={(n) => r.q12AnswersCount(n)(lang)}
                />
            )}
            {!resultsHidden &&
                hasTeam &&
                (data.groups ?? []).map((group) => (
                    <section key={group.groupId} className="q12r-section">
                        <h2 className="q12r-section__title">
                            {group.groupName}
                            {' — '}
                            {r.q12Respondents(group.totalResponses)(lang)}
                        </h2>
                        <Q12QuestionTable
                            rows={toTableRows(group.results)}
                            labels={questionTableLabels}
                            showPrevious={showPrevious}
                        />
                        <Q12TextAnswers
                            title={`${r.q12TextAnswers(lang)}: ${group.groupName}`}
                            blocks={toTextBlocks(group.textAnswers)}
                            countLabel={(n) => r.q12AnswersCount(n)(lang)}
                        />
                        {renderTeamExtras ? (
                            renderTeamExtras(group)
                        ) : group.notes ? (
                            <p className="q12r-note">
                                <strong>{r.q12Notes(lang)}:</strong> {group.notes}
                            </p>
                        ) : null}
                    </section>
                ))}
        </>
    )
}
